import React, { useState } from 'react';
import { loot } from '../data/loot';

const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.replace(/[<>&"']/g, (match) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&#x27;',
  }[match]));
};

function Inventory({ inventory, setPlayer, setLog, canSell, playerLevel }) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const getSellPrice = (item, level) => {
    const basePrice = {
      common: 2,
      uncommon: 5,
      rare: 10,
      epic: 20,
      legendary: 50,
    };
    const inferredRarity = sanitizeInput(item.rarity?.toLowerCase()) || 
                          (item.atk > 5 || item.def > 5 ? 'rare' : 
                          item.hp > 10 || item.mana > 10 ? 'uncommon' : 'common');
    const base = basePrice[inferredRarity] || 2;
    const levelMultiplier = 1 + Math.max(0, level) * 0.1;
    const variation = 0.9 + Math.random() * 0.2;
    return Math.round(Math.max(1, base * levelMultiplier * variation));
  };

  const displayItemStats = (item) => {
    const stats = [];
    if (item.hp) stats.push(`+${item.hp} HP`);
    if (item.mana) stats.push(`+${item.mana} Mana`);
    if (item.stamina) stats.push(`+${item.stamina} Stamina`);
    if (item.atk) stats.push(`+${item.atk} ATK`);
    if (item.def) stats.push(`+${item.def} DEF`);
    if (item.damage) stats.push(`Damage: ${item.damage}`);
    if (item.crit_chance) stats.push(`+${(item.crit_chance * 100).toFixed(0)}% Crit Chance`);
    if (item.crit_damage) stats.push(`+${(item.crit_damage * 100).toFixed(0)}% Crit Damage`);
    if (item.dodge_chance) stats.push(`+${(item.dodge_chance * 100).toFixed(0)}% Dodge Chance`);
    if (item.status_resist) stats.push(`+${(item.status_resist * 100).toFixed(0)}% Status Resist`);
    if (item.luck) stats.push(`+${item.luck} Luck`);
    if (item.range && item.range > 0) stats.push(`Range: ${item.range} tile${item.range > 1 ? 's' : ''}`);
    return stats.length > 0 ? `(${stats.join(', ')})` : '';
  };

  const handleItemUse = (item, index) => {
    if (item.type.toLowerCase() === 'consumable') {
      setPlayer((prev) => {
        const newPlayer = { ...prev };
        if (item.hp) {
          newPlayer.hp = Math.min(Math.round(newPlayer.hp + item.hp), newPlayer.maxHp);
          setLog((prevLog) => [...prevLog, `Used ${sanitizeInput(item.name)} to restore ${item.hp} HP!`]);
        } else if (item.mana) {
          newPlayer.mana = Math.min(Math.round(newPlayer.mana + item.mana), newPlayer.max_mana);
          setLog((prevLog) => [...prevLog, `Used ${sanitizeInput(item.name)} to restore ${item.mana} Mana!`]);
        } else if (item.stamina) {
          newPlayer.stamina = Math.min(Math.round(newPlayer.stamina + item.stamina), calculateInitialStamina(newPlayer.class));
          setLog((prevLog) => [...prevLog, `Used ${sanitizeInput(item.name)} to restore ${item.stamina} Stamina!`]);
        }
        newPlayer.inventory = newPlayer.inventory.filter((_, i) => i !== index);
        return newPlayer;
      });
    } else if (item.equip_slot) {
      setPlayer((prev) => {
        const newPlayer = { ...prev };
        const newInventory = [...newPlayer.inventory];
        const slotMapping = {
          'main_hand': 'weapon',
          'ring': 'rings',
          'chest': 'chest',
          'head': 'head',
          'feet': 'feet',
          'off_hand': 'offHand',
          'neck': 'neck',
          'bracelet': 'bracelet',
          'hands': 'hands',
        };
        const slot = slotMapping[item.equip_slot] || item.equip_slot;

        if (!slot) {
          setLog((prevLog) => [...prevLog, `${sanitizeInput(item.name)} cannot be equipped (no equip slot defined)!`]);
          return prev;
        }

        if (newPlayer.equipped[slot]) {
          if (newInventory.length >= 5) {
            setLog((prevLog) => [...prevLog, `Cannot unequip ${sanitizeInput(newPlayer.equipped[slot].name)}: Inventory full!`]);
            return prev;
          }
          newInventory.push(newPlayer.equipped[slot]);
          const unequippedItem = newPlayer.equipped[slot];
          setLog((prevLog) => [...prevLog, `Unequipped ${sanitizeInput(unequippedItem.name)} and returned it to inventory.`]);
          newPlayer.atk = Math.max(0, (newPlayer.atk || 0) - (unequippedItem.atk || 0));
          newPlayer.def = Math.max(0, (newPlayer.def || 0) - (unequippedItem.def || 0));
          newPlayer.range = Math.max(0, (newPlayer.range || 1) - (unequippedItem.range || 0));
        }

        newPlayer.equipped = { ...newPlayer.equipped, [slot]: item };
        if (item.atk) {
          newPlayer.atk = Math.max(0, (newPlayer.atk || 0) + item.atk);
          setLog((prevLog) => [...prevLog, `Equipped ${sanitizeInput(item.name)} for +${item.atk} ATK in ${slot} slot!`]);
        }
        if (item.def) {
          newPlayer.def = Math.max(0, (newPlayer.def || 0) + item.def);
          setLog((prevLog) => [...prevLog, `Equipped ${sanitizeInput(item.name)} for +${item.def} DEF in ${slot} slot!`]);
        }
        if (item.range) {
          newPlayer.range = Math.max(1, (newPlayer.range || 1) + item.range);
          setLog((prevLog) => [...prevLog, `Equipped ${sanitizeInput(item.name)} for Range: ${item.range} tile${item.range > 1 ? 's' : ''} in ${slot} slot!`]);
        }

        newPlayer.inventory = newInventory.filter((_, i) => i !== index);
        return newPlayer;
      });
    } else {
      setLog((prevLog) => [...prevLog, `${sanitizeInput(item.name)} cannot be used or equipped!`]);
    }
  };

  const handleDiscard = (index) => {
    setPlayer((prev) => ({
      ...prev,
      inventory: prev.inventory.filter((_, i) => i !== index),
    }));
    setLog((prevLog) => [...prevLog, `Discarded ${sanitizeInput(inventory[index].name)}!`]);
  };

  const handleSell = (index) => {
    const item = inventory[index];
    const sellValue = getSellPrice(item, playerLevel);
    setPlayer((prev) => ({
      ...prev,
      inventory: prev.inventory.filter((_, i) => i !== index),
      gold: Math.max(0, prev.gold + sellValue),
    }));
    setLog((prevLog) => [...prevLog, `Sold ${sanitizeInput(item.name)} for ${sellValue} gold!`]);
  };

  const calculateInitialStamina = (className) => {
    switch (className) {
      case 'Warrior': return 100;
      case 'Rogue': return 80;
      case 'Mage': return 90;
      default: return 100;
    }
  };

  // Function to handle waypoint rewards
  const handleWaypointRewards = (waypoint) => {
    if (waypoint.rewards.length === 0) return;

    setPlayer((prev) => {
      const newInventory = [...prev.inventory];
      waypoint.rewards.forEach((reward) => {
        const lootItem = loot.find((item) => item.id === reward.loot_id);
        if (lootItem) {
          if (newInventory.length < 5) {
            newInventory.push(lootItem);
            setLog((prevLog) => [...prevLog, `Received ${lootItem.name} from ${waypoint.name}!`]);
          } else {
            setLog((prevLog) => [...prevLog, `Inventory full! Cannot receive ${lootItem.name} from ${waypoint.name}.`]);
          }
        }
      });
      return { ...prev, inventory: newInventory };
    });
  };

  return (
    <div className="inventory">
      <div className="panel-header">
        <h3>Inventory (Max 5 slots)</h3>
        <button className="collapse-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>
      {!isCollapsed && (
        <>
          {inventory.length === 0 ? (
            <p>Empty</p>
          ) : (
            <ul>
              {inventory.map((item, index) => (
                <li key={index} className="inventory-item">
                  <p>{sanitizeInput(item.name)} {displayItemStats(item)}</p>
                  <span className="tooltip">{item.description || 'No description available'}</span>
                  {(item.hp || item.mana || item.stamina) && (
                    <button className="inventory-button inventory-use" onClick={() => handleItemUse(item, index)}>Use</button>
                  )}
                  {(item.atk || item.def || item.range) && (
                    <button className="inventory-button inventory-equip" onClick={() => handleItemUse(item, index)}>Equip</button>
                  )}
                  <button className="inventory-button inventory-discard" onClick={() => handleDiscard(index)}>Discard</button>
                  {canSell && (
                    <button className="inventory-button inventory-sell" onClick={() => handleSell(index)}>
                      Sell ({getSellPrice(item, playerLevel)} Gold)
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

export default Inventory;